/**
 * 专注计时器：选择时长与关联任务 → startFocus 创建会话 → 倒计时 → endFocus 结束。
 * startedAt/endedAt 可选（服务端默认当前时间），仅在倒计时结束自动提交时显式带 endedAt。
 */
import { useEffect, useRef, useState } from 'react';
import { useEndFocus, useStartFocus, useTasks } from '../hooks/useTasks';
import type { FocusSession } from '../lib/types';
import { formatCountdown } from '../lib/utils';
import { Button, Card, ErrorBox, Field, Select, errMsg } from './ui';
import { Icon } from './icons';

const PRESETS = [15, 25, 45, 60];

interface FocusTimerProps {
  onFinished?: (session: FocusSession) => void;
}

export function FocusTimer({ onFinished }: FocusTimerProps) {
  const [minutes, setMinutes] = useState(25);
  const [taskId, setTaskId] = useState('');
  const [remaining, setRemaining] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 用 ref 保存 finish 最新引用，供归零副作用调用（避免闭包过期/重复提交）
  const finishRef = useRef<() => void>(() => {});

  const { data: tasks } = useTasks({ pageSize: 50 });
  // 客户端过滤未完成任务（TaskStatus 无 'OPEN'，合法值为 TODO/DOING/DONE/CANCELLED）
  const openTasks = (tasks?.items ?? []).filter((t) => t.status === 'TODO' || t.status === 'DOING');
  const startFocus = useStartFocus();
  const endFocus = useEndFocus();

  // 倒计时：running 时每秒递减（纯更新，副作用不放 updater 内，兼容 StrictMode）
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  async function finish() {
    if (!sessionId) return;
    setError(null);
    try {
      const ended = await endFocus.mutateAsync({ id: sessionId, body: { endedAt: new Date().toISOString() } });
      setRunning(false);
      setSessionId(null);
      setRemaining(minutes * 60);
      onFinished?.(ended);
    } catch (e) {
      setError(errMsg(e));
    }
  }

  finishRef.current = () => {
    void finish();
  };

  // 归零自动结束（effect 每次状态变化最多触发一次）
  useEffect(() => {
    if (running && remaining === 0) finishRef.current();
  }, [running, remaining]);

  async function start() {
    setError(null);
    try {
      const session = await startFocus.mutateAsync(taskId ? { taskId } : {});
      setSessionId(session.id);
      setRemaining(minutes * 60);
      setRunning(true);
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <Card className="flex flex-col items-center gap-4 p-6">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400">
        <Icon name="focus" className="h-5 w-5 text-indigo-500" />
        专注计时
      </div>
      <div className="font-mono text-5xl font-semibold tabular-nums tracking-wider text-slate-900 dark:text-white">
        {formatCountdown(remaining)}
      </div>
      <ErrorBox error={error} className="w-full" />
      {!running ? (
        <div className="w-full space-y-3">
          <div className="flex justify-center gap-2">
            {PRESETS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMinutes(m);
                  setRemaining(m * 60);
                }}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  minutes === m
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                }`}
              >
                {m} 分钟
              </button>
            ))}
          </div>
          <Field label="关联任务（可选）">
            <Select value={taskId} onChange={(e) => setTaskId(e.target.value)}>
              <option value="">不关联</option>
              {(openTasks).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </Select>
          </Field>
          <Button className="w-full" onClick={start} loading={startFocus.isPending}>
            开始专注
          </Button>
        </div>
      ) : (
        <div className="w-full space-y-3">
          <div className="text-center text-sm text-slate-500 dark:text-slate-400">
            {taskId ? `关联任务：${openTasks.find((t) => t.id === taskId)?.title ?? ''}` : '未关联任务'}
          </div>
          <Button className="w-full" variant="danger" onClick={finish} loading={endFocus.isPending}>
            结束并记录
          </Button>
        </div>
      )}
    </Card>
  );
}
